import React, { useState } from "react";
import {
  Card,
  CardBody,
  Badge,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import PurchaseReturnService from "../../services/OrderingServices/PurchaseReturn";
import ResponsiveDataManagement from "../../Components/Common/ResponsiveManagementLayout";
import useServerSideSearch from "../../hooks/useServerSideSearch";
import NumberWithCommas from "../../pages/Pages/Starter/NumberWithCommas";
import { formatDateTimes } from "../../Components/Common/FormatDate";

const statusColor = (status) => {
  switch (status?.toLowerCase()) {
    case "approved": return "success";
    case "rejected": return "danger";
    case "pending": return "warning";
    default: return "secondary";
  }
};

const PurchaseReturn = () => {
  const navigate = useNavigate();
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const {
    data: returnList,
    loading,
    totalRows,
    currentPage,
    pageSize,
    searchQuery,
    handleSearch,
    handlePaginationChange,
  } = useServerSideSearch(PurchaseReturnService.getList);

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * pageSize + index + 1,
      width: "70px",
      sortable: false,
    },
    {
      name: "Return Ref No",
      selector: (row) => row.returnReferenceNo,
      cell: (row) => (
        <span
          style={{ cursor: "pointer", color: "#0d6efd" }}
          onClick={() => navigate(`/purchase-returns/detail/${row.id}`)}
          title={row.returnReferenceNo}
          className="text-truncate"
        >
          {row.returnReferenceNo}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Date (BS)",
      selector: (row) => row.returnDate_Nep,
      cell: (row) => row.returnDate_Nep || formatDateTimes(row.returnDate),
      sortable: true,
    },
    {
      name: "Supplier",
      selector: (row) => row.supplierName,
      sortable: true,
    },
    {
      name: "Purchase Ref",
      selector: (row) => row.purchaseReferenceNo,
      cell: (row) => (
        <span className="text-truncate" title={row.purchaseReferenceNo}>
          {row.purchaseReferenceNo}
        </span>
      ),
      sortable: true,
    },
    {
      name: "Total Amount",
      selector: (row) => row.totalReturnAmount,
      cell: (row) => (
        <span className="fw-semibold text-success">
          Rs. <NumberWithCommas number={row.totalReturnAmount} />
        </span>
      ),
      right: true,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      cell: (row) => (
        <Badge color={statusColor(row.status)} pill>
          {row.status}
        </Badge>
      ),
      center: true,
      width: "120px",
      sortable: true,
    },
  ];

  const stats = [
    {
      title: "Total Returns",
      value: totalRows || 0,
      color: "#6366f1",
      icon: "bx bx-undo",
    },
  ];

  const handleAdd = () => navigate("/purchase-returns/create");
  const handleView = (item) => navigate(`/purchase-returns/detail/${item.id}`);

  return (
    <>
      <ResponsiveDataManagement
        title="Purchase Returns"
        pageTitle="Inventory Management"
        mobileCardTitle="Purchase Return List"
        data={returnList}
        columns={columns}
        stats={stats}
        searchKeys={["returnReferenceNo", "supplierName", "purchaseReferenceNo", "status"]}
        onAdd={handleAdd}
        onView={handleView}
        loading={loading}
        emptyStateMessage="No purchase returns found"
        totalRows={totalRows}
        currentPage={currentPage}
        pageSize={pageSize}
        onSearch={handleSearch}
        searchTerm={searchQuery}
        onPaginationChange={handlePaginationChange}
        hideEdit
        hideDelete
        mobileCardRenderer={(item, index) => (
          <Card
            key={item.id}
            className="shadow-sm rounded-3 border-0 border-start border-4 border-primary mb-3"
            onClick={() => handleView(item)}
            style={{ cursor: "pointer" }}
          >
            <CardBody>
              <div className="d-flex justify-content-between align-items-start gap-2">
                <div className="flex-grow-1">
                  <h6 className="fw-bold text-primary mb-1">
                    {item.returnReferenceNo}
                  </h6>
                  <p className="mb-1 text-muted small">
                    <strong>Date (BS):</strong>{" "}
                    {item.returnDate_Nep || formatDateTimes(item.returnDate)}
                  </p>
                  <p className="mb-1 text-muted small">
                    <strong>Supplier:</strong> {item.supplierName}
                  </p>
                  <p className="mb-1 text-muted small">
                    <strong>Purchase Ref:</strong> {item.purchaseReferenceNo}
                  </p>
                  <p className="mb-1 text-muted small">
                    <strong>Total Amount:</strong>{" "}
                    <span className="text-success fw-semibold">
                      Rs. <NumberWithCommas number={item.totalReturnAmount} />
                    </span>
                  </p>
                  <div className="mt-2">
                    <Badge color={statusColor(item.status)} pill>
                      {item.status}
                    </Badge>
                  </div>
                </div>

                <Dropdown
                  isOpen={openDropdownId === item.id}
                  toggle={() =>
                    setOpenDropdownId(openDropdownId === item.id ? null : item.id)
                  }
                  direction="down"
                >
                  <DropdownToggle
                    color="light"
                    className="btn-sm rounded-circle shadow-sm"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="ri-more-2-fill" />
                  </DropdownToggle>
                  <DropdownMenu end>
                    <DropdownItem
                      onClick={(e) => {
                        e.stopPropagation();
                        handleView(item);
                        setOpenDropdownId(null);
                      }}
                    >
                      <i className="ri-eye-line me-2" />
                      View Detail
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </CardBody>
          </Card>
        )}
      />
    </>
  );
};

export default PurchaseReturn;
